import { Box, Button, Flex, Heading, Image, Tag, Text } from "@chakra-ui/react"
import { BlockProductFeedEntityHydrated, EntityId } from "@jackfruit/common"
import React from "react"
import { useTranslation } from "react-i18next"
import { useEffectOnce, useLocation } from "react-use"
import { useApplicationState } from "~/hooks/useApplicationState"
import { usePageCart } from "~/hooks/usePageCart"
import { usePrintService } from "~/hooks/usePrintService"
import { useProcessActions } from "~/hooks/useProcessActions"
import { useProduct } from "~/hooks/useProduct"
import { useProductFeedBlock } from "~/hooks/useProductFeedBlock"
import { useProductPrice } from "~/hooks/useProductPrice"
import { useSetPageMetaForProduct } from "./hooks/useSetPageMetaForProduct"

interface Props {
  pageId: EntityId
  config: BlockProductFeedEntityHydrated
}

const ProductFeedContent: React.FC<Props> = ({ pageId, config }) => {
  const { id: blockId } = config
  const { productFeedBlock } = useProductFeedBlock(blockId)
  const {
    productId = 0,
    templateId = 0,
    isLoadingBuyNow = false,
    isLoadingUploadPhoto = false,
    imageRegionsCount: requiredImageRegionsCount = 1,
    desktopIllustrationUrl,
    mobileIllustrationUrl,
  } = productFeedBlock || {}
  useSetPageMetaForProduct(productId, pageId)
  const process = useProcessActions()
  const { t } = useTranslation()
  const { html, buyNowButtonLabel, uploadPhotoButtonLabel } = config
  const { displayName } = useApplicationState()
  const { href: currentUrl } = useLocation()
  const { product } = useProduct(productId)

  const {
    id: internalProductId,
    longDescription,
    shortDescription,
    printServiceId,
    categoryName,
    size,
    finish,
    metaData,
    prices,
  } = product
  const { gtin } = metaData
  const { printService } = usePrintService(printServiceId)
  const { fulfillmentType } = printService
  const { productPrice } = useProductPrice(prices[0])
  const { currency, total, totalHuman } = productPrice
  const { pageCart } = usePageCart()

  useEffectOnce(() => {
    if (fulfillmentType === "pickup") {
      const needToPrepopulateStoreSearch = !Boolean(pageCart.storeId)
      // initialize the stores for pickup fulfillment
      if (needToPrepopulateStoreSearch) {
        process.initStores({ productId: internalProductId, printServiceId })
      }
    }
  })

  const shouldOpenEditor = requiredImageRegionsCount > 1
  const shouldShowUploadPhotoButton = requiredImageRegionsCount > 0

  const buyNowButtonHandler = () => {
    if (templateId) {
      process.createLineItemForProductIdAndTemplateIdWithPlaceholderImage({
        productId,
        templateId,
        productFeedBlockId: blockId,
      })
    } else {
      process.createLineItemForProductIdWithPlaceholderImage({
        productId,
        productFeedBlockId: blockId,
      })
    }
  }

  const customizeButtonHandler = () => {
    if (templateId) {
      if (shouldOpenEditor) {
        process.createLineItemForProductIdAndTemplateId({
          productId,
          templateId,
          shouldOpenEditor: true,
          productFeedBlockId: blockId,
        })
      } else {
        process.uploadImagesAndCreateLineItemsForProductIdAndTemplateId({
          productId,
          templateId,
          productFeedBlockId: blockId,
        })
      }
    } else {
      if (shouldOpenEditor) {
        process.createLineItemForProductId({
          productId,
          shouldOpenEditor: true,
          productFeedBlockId: blockId,
        })
      } else {
        process.uploadImagesAndCreateLineItemsForProductId({
          productId,
          productFeedBlockId: blockId,
        })
      }
    }
  }

  const jsonLdContent = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: shortDescription,
    image: desktopIllustrationUrl,
    description: longDescription,
    sku: productId,
    gtin14: gtin,
    brand: {
      "@type": "Brand",
      name: displayName,
    },
    offers: {
      "@type": "Offer",
      url: currentUrl,
      priceCurrency: currency,
      price: total,
      itemCondition: "https://schema.org/NewCondition",
      availability: "https://schema.org/InStock",
    },
  }

  const uploadPhotoButtonLabelToUse =
    uploadPhotoButtonLabel ||
    t("components.blocks.product-feed.ProductFeedContent.uploadPhoto")

  const buyNowButtonLabelToUse =
    buyNowButtonLabel ||
    t("components.blocks.product-feed.ProductFeedContent.getStarted")

  const fulfilmentLabel =
    fulfillmentType === "pickup"
      ? t("components.blocks.product-feed.ProductFeedContent.tags.pickup")
      : t("components.blocks.product-feed.ProductFeedContent.tags.delivery")

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(jsonLdContent, null, 2)}
      </script>
      <Flex
        gap={{ base: 6, md: 10 }}
        py="2rem"
        flexDirection={{ base: "column", md: "row" }}
      >
        <Flex width={{ base: "full", md: "80%" }} justifyContent="center">
          <Image
            src={desktopIllustrationUrl}
            alt={shortDescription}
            title={shortDescription}
            htmlHeight="400px"
            height="400px"
            borderRadius="xl"
            objectFit="contain"
            display={{ base: "none", md: "block" }}
          />
          <Image
            src={mobileIllustrationUrl}
            alt={shortDescription}
            title={shortDescription}
            htmlWidth="400px"
            htmlHeight="200px"
            boxSize="200px"
            width={{ base: "full", md: "400px" }}
            borderRadius="xl"
            objectFit="cover"
            display={{ base: "block", md: "none" }}
          />
        </Flex>
        <Flex flexDirection="column" gap={4} w="full">
          <Heading as="h1" size="lg" order={{ base: 1 }}>
            {shortDescription}
          </Heading>

          <Flex gap={3} order={{ base: 2 }}>
            <Tag textTransform="capitalize">{categoryName}</Tag>
            <Tag textTransform="capitalize">
              {t("components.blocks.product-feed.ProductFeedContent.inStock")}
            </Tag>
            <Tag>{totalHuman}</Tag>
            <Tag textTransform="capitalize">{fulfilmentLabel}</Tag>
          </Flex>

          {Boolean(html) && (
            <Box
              order={{ base: 3 }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}

          <Heading as="h2" size="md" order={{ base: 10, md: 10 }}>
            {t("components.blocks.product-feed.ProductFeedContent.features")}
          </Heading>

          <Flex flexDirection="column" gap={2} order={{ base: 11, md: 11 }}>
            <Flex>
              <Text fontWeight="bold">
                {t("components.blocks.product-feed.ProductFeedContent.sizes")} :
              </Text>
              <Text ml={1}>{size}</Text>
            </Flex>
            <Flex>
              <Text fontWeight="bold">
                {t("components.blocks.product-feed.ProductFeedContent.finish")}{" "}
                :
              </Text>
              <Text ml={1}>{finish}</Text>
            </Flex>
          </Flex>

          <Flex
            py={2}
            gap={3}
            flexDirection={{ base: "column", md: "row" }}
            order={{ base: 9, md: 12 }}
          >
            {shouldShowUploadPhotoButton && (
              <Button
                id="product-customize-button"
                size="lg"
                w={{ base: "full", md: "auto" }}
                colorScheme="primary"
                isLoading={isLoadingUploadPhoto}
                onClick={customizeButtonHandler}
              >
                {uploadPhotoButtonLabelToUse}
              </Button>
            )}
            <Button
              id="product-buy-now-button"
              size="lg"
              w={{ base: "full", md: "auto" }}
              variant="outline"
              colorScheme="primary"
              _hover={{ bg: "transparent" }}
              isLoading={isLoadingBuyNow}
              onClick={buyNowButtonHandler}
            >
              {buyNowButtonLabelToUse}
            </Button>
          </Flex>

          <Heading as="h2" size="md" order={{ base: 13 }}>
            {t("components.blocks.product-feed.ProductFeedContent.description")}
          </Heading>
          <Flex minHeight="120px" order={{ base: 14 }}>
            <Box dangerouslySetInnerHTML={{ __html: longDescription }} />
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

export default ProductFeedContent
